import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API_BASE } from '@/context/AuthContext';
import AppHeader from '@/components/AppHeader';
import SearchableSelect from '@/components/SearchableSelect';
import {
  User,
  FileText,
  UserPlus,
  Beaker,
  Pill,
  BedDouble,
  ScanLine,
  Save,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Loader2,
  Edit3,
  IndianRupee,
  RotateCcw,
  Percent,
  HandCoins,
  History,
  Lock,
  Calendar,
  ClipboardList,
  Plus,
  Trash2,
  Search,
} from 'lucide-react';

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  patient_name: '',
  referral_date: todayISO(),
  op_number: '',
  village: '',
  referral_doctor_id: '',
  marketing_executive_id: '',
  consultant_id: '',
  scan_total_amount: '',
  scan_referral_amount: '',
  lab_method: 'percentage',
  lab_total_amount: '',
  lab_kit_amount: '',
  lab_percentage: '',
  lab_goodwill_amount: '',
  pharmacy_method: 'percentage',
  pharmacy_total_amount: '',
  pharmacy_kit_amount: '',
  pharmacy_percentage: '',
  pharmacy_goodwill_amount: '',
  ip_method: 'percentage',
  ip_amount: '',
  ip_deductibles: '',
  ip_percentage: '',
  ip_goodwill_amount: '',
};

const toNum = (v) => {
  if (v === '' || v === null || v === undefined) return 0;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n) => Math.round(n * 100) / 100;

const formatINR = (n) => {
  const num = Number(n) || 0;
  return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const MAX_AMOUNT = 1000000;

export default function ReferralEntryPage() {
  const { referralId } = useParams();
  const isEdit = !!referralId;
  const navigate = useNavigate();
  const {
    user, isAdmin, isReception, isLabStaff, isPharmacist, getAuthHeader,
  } = useAuth();

  // Role-based visibility flags (spec §9-13)
  //   Reception:  Patient + Scan + IP editable; Lab + Pharmacy hidden
  //   Lab Staff:  Patient read-only; Lab editable; Scan/Pharmacy/IP hidden
  //   Pharmacist: Patient read-only; Pharmacy editable; Scan/Lab/IP hidden
  //   Admin/staff: full form
  const canEditPatient = isAdmin || isReception || (!isEdit && user?.role === 'staff');
  const canEditScan = isAdmin || isReception || (!isEdit && user?.role === 'staff');
  const canEditLab = isAdmin || isLabStaff || (!isEdit && user?.role === 'staff');
  const canEditPharmacy = isAdmin || isPharmacist || (!isEdit && user?.role === 'staff');
  const canEditIP = isAdmin || isReception || (!isEdit && user?.role === 'staff');

  // Section visibility
  const showPatient = true; // always visible (locked for lab/pharm on edit)
  const showScan = isAdmin || isReception || user?.role === 'staff';
  const showLab = isAdmin || isLabStaff || user?.role === 'staff';
  const showPharmacy = isAdmin || isPharmacist || user?.role === 'staff';
  const showIP = isAdmin || isReception || user?.role === 'staff';

  // Reception/Lab/Pharmacy roles should not see any computed referral amounts (per-section or final total).
  const canSeeTotals = !(isReception || isLabStaff || isPharmacist);

  const [form, setForm] = useState(emptyForm);
  const [doctors, setDoctors] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [consultants, setConsultants] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedResult, setSavedResult] = useState(null);
  const [loadedReferral, setLoadedReferral] = useState(null);

  // Visit-type flow (only when creating a new referral)
  const [visitType, setVisitType] = useState('FIRST_VISIT');
  const [followUpOp, setFollowUpOp] = useState('');
  const [followUpDate, setFollowUpDate] = useState(todayISO());
  const [followUpLookup, setFollowUpLookup] = useState(null); // {patient, defaults, history} once found
  const [followUpBusy, setFollowUpBusy] = useState(false);
  const [followUpError, setFollowUpError] = useState('');

  // Procedures — only reception/admin can add/edit; visible to all with the referral.
  // While creating a new referral (no ID yet), procedures live in local state and
  // are POSTed after the parent referral is created.
  const [procedures, setProcedures] = useState([]); // {id?, procedure_name, procedure_amount, referral_amount, _local?}
  const [procDraftName, setProcDraftName] = useState('');
  const [procDraftAmount, setProcDraftAmount] = useState('');
  const canManageProcedures = isAdmin || isReception;

  const loadProceduresFromServer = async (parentId) => {
    if (!parentId) return;
    try {
      const res = await axios.get(`${API_BASE}/referrals/${parentId}/procedures`, { headers: getAuthHeader() });
      setProcedures((res.data || []).map(p => ({
        id: p.id,
        procedure_name: p.procedure_name,
        procedure_amount: p.procedure_amount,
        referral_amount: p.referral_amount,
      })));
    } catch (err) {
      // silent — procedures section will just show empty
    }
  };

  const addProcedureRow = async () => {
    const name = (procDraftName || '').trim();
    const amt = parseFloat(procDraftAmount);
    if (!name) { setError('Procedure name is required.'); return; }
    if (!(amt >= 0)) { setError('Procedure amount must be zero or greater.'); return; }
    setError('');
    if (isEdit && loadedReferral?.id) {
      try {
        const res = await axios.post(
          `${API_BASE}/referrals/${loadedReferral.id}/procedures`,
          { procedure_name: name, procedure_amount: amt },
          { headers: getAuthHeader() }
        );
        setProcedures(p => [...p, {
          id: res.data.id,
          procedure_name: res.data.procedure_name,
          procedure_amount: res.data.procedure_amount,
          referral_amount: res.data.referral_amount,
        }]);
        setProcDraftName(''); setProcDraftAmount('');
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to add procedure.');
      }
    } else {
      setProcedures(p => [...p, {
        _local: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        procedure_name: name,
        procedure_amount: amt,
        referral_amount: 0,
      }]);
      setProcDraftName(''); setProcDraftAmount('');
    }
  };

  const removeProcedureRow = async (row) => {
    if (row.id && loadedReferral?.id) {
      try {
        await axios.delete(`${API_BASE}/referrals/${loadedReferral.id}/procedures/${row.id}`, { headers: getAuthHeader() });
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to remove procedure.');
        return;
      }
    }
    setProcedures(p => p.filter(x => (x.id || x._local) !== (row.id || row._local)));
  };

  // Load master lists once (active only)
  useEffect(() => {
    let cancelled = false;
    const loadAll = async () => {
      setLoading(true);
      try {
        const [docsRes, execsRes, consRes] = await Promise.all([
          axios.get(`${API_BASE}/referral-doctors?status_filter=active`, { headers: getAuthHeader() }),
          axios.get(`${API_BASE}/marketing-executives?status_filter=active`, { headers: getAuthHeader() }),
          axios.get(`${API_BASE}/inhouse-consultants?status_filter=active`, { headers: getAuthHeader() }),
        ]);
        if (cancelled) return;
        setDoctors((docsRes.data || []).map((d) => ({
          id: d.id,
          name: d.name,
          subtitle: [d.village, d.phone_number].filter(Boolean).join(' · ') || 'Referral Doctor',
        })));
        setExecutives((execsRes.data || []).map((d) => ({
          id: d.id,
          name: d.name,
          subtitle: [d.village_area, d.phone_number].filter(Boolean).join(' · ') || 'Marketing Executive',
        })));
        setConsultants((consRes.data || []).map((d) => ({
          id: d.id,
          name: d.name,
          subtitle: d.specialty || 'Consultant',
        })));
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Failed to load reference lists.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadAll();
    return () => { cancelled = true; };
  }, [getAuthHeader]);

  // If edit mode, load existing referral
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    const loadReferral = async () => {
      try {
        const res = await axios.get(`${API_BASE}/referrals/${referralId}`, { headers: getAuthHeader() });
        if (cancelled) return;
        const r = res.data;
        setLoadedReferral(r);
        setForm({
          patient_name: r.patient_name || '',
          referral_date: r.referral_date || '',
          op_number: r.op_number || '',
          village: r.village || '',
          referral_doctor_id: r.referral_doctor_id || '',
          marketing_executive_id: r.marketing_executive_id || '',
          consultant_id: r.consultant_id || '',
          scan_total_amount: r.scan_total_amount || '',
          scan_referral_amount: r.scan_referral_amount || '',
          lab_method: r.lab_method || 'percentage',
          lab_total_amount: r.lab_total_amount || '',
          lab_kit_amount: r.lab_kit_amount || '',
          lab_percentage: r.lab_percentage || '',
          lab_goodwill_amount: r.lab_goodwill_amount || '',
          pharmacy_method: r.pharmacy_method || 'percentage',
          pharmacy_total_amount: r.pharmacy_total_amount || '',
          pharmacy_kit_amount: r.pharmacy_kit_amount || '',
          pharmacy_percentage: r.pharmacy_percentage || '',
          pharmacy_goodwill_amount: r.pharmacy_goodwill_amount || '',
          ip_method: r.ip_method || 'percentage',
          ip_amount: r.ip_amount || '',
          ip_deductibles: r.ip_deductibles || '',
          ip_percentage: r.ip_percentage || '',
          ip_goodwill_amount: r.ip_goodwill_amount || '',
        });
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Failed to load referral for editing.');
      }
    };
    loadReferral();
    loadProceduresFromServer(referralId);
    return () => { cancelled = true; };
  }, [isEdit, referralId, getAuthHeader]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Live computed values
  const calc = useMemo(() => {
    const scanTotal = toNum(form.scan_total_amount);
    const scanRef = toNum(form.scan_referral_amount);

    // Lab
    const labMethod = form.lab_method || 'percentage';
    let labTotal = 0, labKit = 0, labNet = 0, labPct = 0, labGw = 0, labRef = 0;
    if (labMethod === 'goodwill') {
      labGw = toNum(form.lab_goodwill_amount);
      labRef = round2(labGw);
    } else {
      labTotal = toNum(form.lab_total_amount);
      labKit = toNum(form.lab_kit_amount);
      labPct = toNum(form.lab_percentage);
      labNet = round2(Math.max(0, labTotal - labKit));
      labRef = round2(labNet * labPct / 100);
    }

    // Pharmacy
    const phMethod = form.pharmacy_method || 'percentage';
    let phTotal = 0, phKit = 0, phNet = 0, phPct = 0, phGw = 0, phRef = 0;
    if (phMethod === 'goodwill') {
      phGw = toNum(form.pharmacy_goodwill_amount);
      phRef = round2(phGw);
    } else {
      phTotal = toNum(form.pharmacy_total_amount);
      phKit = toNum(form.pharmacy_kit_amount);
      phPct = toNum(form.pharmacy_percentage);
      phNet = round2(Math.max(0, phTotal - phKit));
      phRef = round2(phNet * phPct / 100);
    }

    // IP
    const ipMethod = form.ip_method || 'percentage';
    let ipAmt = 0, ipDed = 0, ipNet = 0, ipPct = 0, ipGw = 0, ipRef = 0;
    if (ipMethod === 'goodwill') {
      ipGw = toNum(form.ip_goodwill_amount);
      ipRef = round2(ipGw);
    } else {
      ipAmt = toNum(form.ip_amount);
      ipDed = toNum(form.ip_deductibles);
      ipPct = toNum(form.ip_percentage);
      ipNet = round2(Math.max(0, ipAmt - ipDed));
      ipRef = round2(ipNet * ipPct / 100);
    }

    const final = round2(scanRef + labRef + phRef + ipRef);

    return {
      scanTotal, scanRef,
      labMethod, labTotal, labKit, labNet, labPct, labGw, labRef,
      phMethod, phTotal, phKit, phNet, phPct, phGw, phRef,
      ipMethod, ipAmt, ipDed, ipNet, ipPct, ipGw, ipRef,
      final,
    };
  }, [form]);

  // Inline validation
  const validations = useMemo(() => {
    const errs = {};
    if (canEditPatient) {
      if (!form.patient_name.trim()) errs.patient_name = 'Required';
      if (!form.op_number.trim()) errs.op_number = 'Required';
      if (!form.referral_doctor_id) errs.referral_doctor_id = 'Required';
      if (!form.marketing_executive_id) errs.marketing_executive_id = 'Required';
      if (!form.consultant_id) errs.consultant_id = 'Required';
    }
    // Only Admin sees / edits calculation inputs on this page — restricted roles cannot
    // trigger these validators. All calc fields are zeroed by backend for restricted roles.
    if (isAdmin) {
      if (calc.scanRef > calc.scanTotal) errs.scan_referral_amount = 'Cannot exceed Scan Total Amount';
      if (calc.labMethod === 'percentage') {
        if (calc.labKit > calc.labTotal && calc.labTotal > 0) errs.lab_kit_amount = 'Cannot exceed Lab Total';
        const v = toNum(form.lab_percentage);
        if (v < 0 || v > 100) errs.lab_percentage = 'Must be 0–100';
      } else {
        if (toNum(form.lab_goodwill_amount) > MAX_AMOUNT) errs.lab_goodwill_amount = 'Exceeds ₹10,00,000 cap';
        if (toNum(form.lab_goodwill_amount) < 0) errs.lab_goodwill_amount = 'Cannot be negative';
      }
      if (calc.phMethod === 'percentage') {
        if (calc.phKit > calc.phTotal && calc.phTotal > 0) errs.pharmacy_kit_amount = 'Cannot exceed Pharmacy Total';
        const v = toNum(form.pharmacy_percentage);
        if (v < 0 || v > 100) errs.pharmacy_percentage = 'Must be 0–100';
      } else {
        if (toNum(form.pharmacy_goodwill_amount) > MAX_AMOUNT) errs.pharmacy_goodwill_amount = 'Exceeds ₹10,00,000 cap';
        if (toNum(form.pharmacy_goodwill_amount) < 0) errs.pharmacy_goodwill_amount = 'Cannot be negative';
      }
      if (calc.ipMethod === 'percentage') {
        if (calc.ipDed > calc.ipAmt && calc.ipAmt > 0) errs.ip_deductibles = 'Cannot exceed IP Amount';
        const v = toNum(form.ip_percentage);
        if (v < 0 || v > 100) errs.ip_percentage = 'Must be 0–100';
      } else {
        if (toNum(form.ip_goodwill_amount) > MAX_AMOUNT) errs.ip_goodwill_amount = 'Exceeds ₹10,00,000 cap';
        if (toNum(form.ip_goodwill_amount) < 0) errs.ip_goodwill_amount = 'Cannot be negative';
      }
    } else {
      // For restricted roles — only bounds-check raw amounts they can edit
      if (isReception) {
        if (calc.ipDed > calc.ipAmt && calc.ipAmt > 0) errs.ip_deductibles = 'Cannot exceed IP Amount';
      }
      if (isLabStaff) {
        if (calc.labKit > calc.labTotal && calc.labTotal > 0) errs.lab_kit_amount = 'Cannot exceed Lab Total';
      }
      if (isPharmacist) {
        if (calc.phKit > calc.phTotal && calc.phTotal > 0) errs.pharmacy_kit_amount = 'Cannot exceed Pharmacy Total';
      }
    }
    ['scan_total_amount','lab_total_amount','lab_kit_amount','pharmacy_total_amount','pharmacy_kit_amount','ip_amount','ip_deductibles'].forEach((k) => {
      if (toNum(form[k]) > MAX_AMOUNT) errs[k] = 'Exceeds ₹10,00,000 cap';
      if (toNum(form[k]) < 0) errs[k] = 'Cannot be negative';
    });
    return errs;
  }, [form, calc, canEditPatient, isAdmin, isReception, isLabStaff, isPharmacist]);

  const hasErrors = Object.keys(validations).length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (hasErrors) {
      setError('Please fix the highlighted fields before saving.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        visit_type: visitType,
        patient_name: form.patient_name.trim(),
        referral_date: form.referral_date || todayISO(),
        op_number: form.op_number.trim(),
        village: form.village.trim(),
        referral_doctor_id: form.referral_doctor_id,
        marketing_executive_id: form.marketing_executive_id,
        consultant_id: form.consultant_id,
        scan_total_amount: toNum(form.scan_total_amount),
        scan_referral_amount: toNum(form.scan_referral_amount),
        lab_method: form.lab_method || 'percentage',
        lab_total_amount: toNum(form.lab_total_amount),
        lab_kit_amount: toNum(form.lab_kit_amount),
        lab_percentage: toNum(form.lab_percentage),
        lab_goodwill_amount: toNum(form.lab_goodwill_amount),
        pharmacy_method: form.pharmacy_method || 'percentage',
        pharmacy_total_amount: toNum(form.pharmacy_total_amount),
        pharmacy_kit_amount: toNum(form.pharmacy_kit_amount),
        pharmacy_percentage: toNum(form.pharmacy_percentage),
        pharmacy_goodwill_amount: toNum(form.pharmacy_goodwill_amount),
        ip_method: form.ip_method || 'percentage',
        ip_amount: toNum(form.ip_amount),
        ip_deductibles: toNum(form.ip_deductibles),
        ip_percentage: toNum(form.ip_percentage),
        ip_goodwill_amount: toNum(form.ip_goodwill_amount),
      };

      const url = isEdit ? `${API_BASE}/referrals/${referralId}` : `${API_BASE}/referrals`;
      const method = isEdit ? 'put' : 'post';

      const res = await axios[method](url, payload, { headers: getAuthHeader() });
      // On CREATE by reception/admin — flush any local procedure rows to the new referral
      if (!isEdit && canManageProcedures && procedures.length > 0) {
        const newId = res.data?.referral?.id;
        if (newId) {
          for (const p of procedures) {
            if (p._local) {
              try {
                await axios.post(
                  `${API_BASE}/referrals/${newId}/procedures`,
                  { procedure_name: p.procedure_name, procedure_amount: p.procedure_amount },
                  { headers: getAuthHeader() }
                );
              } catch (e) { /* individual proc failure — surface via reload only */ }
            }
          }
          await loadProceduresFromServer(newId);
        }
      }
      setSavedResult(res.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save referral.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (isEdit) return;
    if (window.confirm('Clear all fields and start fresh?')) {
      setForm(emptyForm);
      setError('');
    }
  };

  const startAnother = () => {
    setSavedResult(null);
    setForm(emptyForm);
    setError('');
    if (isEdit) navigate('/staff/referrals/new', { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading && !isEdit) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
        <AppHeader />
        <div className="flex-1 flex flex-col items-center justify-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
          <p className="text-sm text-slate-400">Loading master lists…</p>
        </div>
      </div>
    );
  }

  // === Success view ===
  if (savedResult) {
    const r = savedResult.referral;
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
        <AppHeader />
        <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-10">
          <div data-testid="referral-saved-card" className="bg-gradient-to-br from-emerald-950/60 via-slate-900 to-teal-950/40 border border-emerald-800/60 rounded-2xl p-8 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Referral Saved Successfully</h2>
                <p className="text-xs text-emerald-300/80 mt-0.5">
                  {isEdit ? `Updated by ${user?.name}` : `Recorded by ${user?.name}`} · {new Date().toLocaleString()}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-slate-950/60 border border-slate-800 p-4 rounded-xl">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Referral ID</div>
                <div data-testid="saved-referral-id" className="text-xl font-bold font-mono text-teal-300 tracking-wide">
                  {r.referral_id}
                </div>
              </div>
              <div className="bg-slate-950/60 border border-slate-800 p-4 rounded-xl">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Patient</div>
                <div data-testid="saved-patient-name" className="text-lg font-semibold text-white truncate">{r.patient_name}</div>
                <div className="text-xs text-slate-500">OP {r.op_number}</div>
              </div>
              {canSeeTotals && (
                <div className="bg-slate-950/60 border border-slate-800 p-4 rounded-xl">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Final Referral Amount</div>
                  <div data-testid="saved-final-amount" className="text-xl font-bold text-emerald-300 flex items-center">
                    <IndianRupee className="w-4 h-4" />
                    {formatINR(r.final_referral_amount)}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              {(isAdmin || isReception || user?.role === 'staff') && (
                <button
                  onClick={startAnother}
                  data-testid="save-another-btn"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-teal-500 hover:bg-teal-400 text-slate-950 text-sm font-semibold rounded-xl shadow-md shadow-teal-500/20 transition-all"
                >
                  <Sparkles className="w-4 h-4" />
                  Enter Another Referral
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => {
                    setSavedResult(null);
                    if (!isEdit) navigate(`/staff/referrals/${r.id}/edit`, { replace: true });
                  }}
                  data-testid="edit-saved-referral-btn"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm font-medium rounded-xl border border-slate-700 transition-all"
                >
                  <Edit3 className="w-4 h-4" />
                  Edit this Referral
                </button>
              )}
              <button
                onClick={() => navigate('/staff/referrals')}
                data-testid="back-to-list-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 text-slate-400 hover:text-white text-sm transition-colors"
              >
                Back to Referrals
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const inputBase = 'w-full px-3.5 py-2.5 bg-slate-950 border rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors';
  const inputClass = (key, disabled) =>
    `${inputBase} ${validations[key] ? 'border-red-800/70' : 'border-slate-800'} ${disabled ? 'opacity-60 cursor-not-allowed bg-slate-900' : ''}`;

  const patientLocked = isEdit && (isLabStaff || isPharmacist);
  // Patient identity fields (Name / OP / Village) are additionally locked when creating a Follow-up
  // referral — reception must not rename the patient or change their OP mid-visit.
  const identityLocked = patientLocked || (!isEdit && visitType === 'FOLLOW_UP' && !!followUpLookup);

  const doLookupFollowUp = async () => {
    setFollowUpError('');
    const op = followUpOp.trim();
    if (!op) { setFollowUpError('Enter the OP No. to search.'); return; }
    setFollowUpBusy(true);
    try {
      const r = await axios.get(`${API_BASE}/patients/lookup`, { params: { op_number: op }, headers: getAuthHeader() });
      setFollowUpLookup(r.data);
    } catch (err) {
      setFollowUpLookup(null);
      setFollowUpError(err.response?.data?.detail || 'Patient not found. Please check the OP No. and try again.');
    } finally {
      setFollowUpBusy(false);
    }
  };

  const continueToFollowUpEntry = () => {
    if (!followUpLookup) return;
    const p = followUpLookup.patient;
    const d = followUpLookup.defaults || {};
    setForm({
      ...emptyForm,
      patient_name: p.patient_name || '',
      op_number: p.op_number || '',
      village: p.village || '',
      referral_date: followUpDate || todayISO(),
      referral_doctor_id: d.referral_doctor_id || '',
      marketing_executive_id: d.marketing_executive_id || '',
      consultant_id: d.consultant_id || '',
    });
  };

  const backToFirstVisit = () => {
    setVisitType('FIRST_VISIT');
    setFollowUpOp('');
    setFollowUpLookup(null);
    setFollowUpError('');
    setForm(emptyForm);
  };

  const shouldShowSearchOnly = !isEdit && visitType === 'FOLLOW_UP' && !followUpLookup;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <AppHeader />

      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-32 space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-teal-500/10 border border-teal-500/20 text-teal-400 flex items-center justify-center shrink-0">
              <FileText className="w-6 h-6" />
            </div>
            <div>
              <h1 data-testid="referral-entry-title" className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                {isEdit
                  ? isLabStaff ? 'Update Lab Section'
                  : isPharmacist ? 'Update Pharmacy Section'
                  : 'Edit Referral'
                  : 'New Referral Entry'}
              </h1>
              <p className="text-slate-400 text-sm mt-1">
                {isEdit && loadedReferral ? (
                  <>
                    <span className="font-mono text-teal-400">{loadedReferral.referral_id}</span> — created by{' '}
                    <span className="text-slate-200">{loadedReferral.created_by}</span> on{' '}
                    {new Date(loadedReferral.created_at).toLocaleString()}
                  </>
                ) : isReception ? (
                  'Enter patient & referral information. Financial figures will be filled in by Lab / Pharmacy staff.'
                ) : (
                  'Record patient, service, and financial details in one place.'
                )}
              </p>
            </div>
          </div>
          {!isEdit && (
            <button
              type="button"
              onClick={handleReset}
              data-testid="referral-reset-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-400 hover:text-white bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-lg transition-colors self-start"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Clear form
            </button>
          )}
        </div>

        {error && (
          <div data-testid="referral-error-alert" className="p-3 bg-red-950/60 border border-red-800/80 rounded-xl text-red-200 text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Method Change Audit Trail (edit mode only) */}
        {isEdit && loadedReferral?.edit_history?.length > 0 && (
          <div data-testid="method-audit-trail" className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-amber-400" />
              <h3 className="text-sm font-semibold text-white">Method Change History</h3>
            </div>
            <ul className="space-y-2">
              {loadedReferral.edit_history.map((h, idx) => (
                <li key={idx} className="text-xs text-slate-400 flex items-start gap-2">
                  <span className="text-amber-300 font-mono">{new Date(h.at).toLocaleString()}</span>
                  <span className="text-slate-200 capitalize">{h.section}</span>
                  <span>changed from</span>
                  <span className="text-slate-200 uppercase">{h.from_method}</span>
                  <span>to</span>
                  <span className="text-slate-200 uppercase">{h.to_method}</span>
                  <span>by</span>
                  <span className="text-teal-300">@{h.by_username}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Visit Type selector — only when creating a new referral, and only for reception/admin */}
        {!isEdit && (isReception || isAdmin) && (
          <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5" data-testid="visit-type-block">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-teal-400" />
              <h3 className="text-sm font-semibold text-white">Visit Type</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="visit-type-first"
                onClick={() => { setVisitType('FIRST_VISIT'); setFollowUpLookup(null); setFollowUpOp(''); setFollowUpError(''); setForm(emptyForm); }}
                className={`p-4 text-left rounded-xl border transition-colors ${visitType === 'FIRST_VISIT' ? 'bg-teal-500/10 border-teal-500/60' : 'bg-slate-950/60 border-slate-800 hover:border-slate-700'}`}
              >
                <div className="text-sm font-semibold text-white">First Visit</div>
                <div className="text-[11px] text-slate-500 mt-1">New patient — register fresh with a new OP No.</div>
              </button>
              <button
                type="button"
                data-testid="visit-type-follow"
                onClick={() => { setVisitType('FOLLOW_UP'); }}
                className={`p-4 text-left rounded-xl border transition-colors ${visitType === 'FOLLOW_UP' ? 'bg-teal-500/10 border-teal-500/60' : 'bg-slate-950/60 border-slate-800 hover:border-slate-700'}`}
              >
                <div className="text-sm font-semibold text-white">Follow-up</div>
                <div className="text-[11px] text-slate-500 mt-1">Existing patient — search by OP No. and create a follow-up.</div>
              </button>
            </div>
          </div>
        )}

        {/* Follow-up patient search step */}
        {shouldShowSearchOnly && (
          <div className="space-y-5" data-testid="follow-up-search-block">
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5">
              <div className="text-sm font-semibold text-white mb-3">Search Existing Patient</div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  data-testid="follow-up-op-input"
                  type="text"
                  value={followUpOp}
                  onChange={(e) => setFollowUpOp(e.target.value)}
                  placeholder="Enter OP No. (e.g. OP12345)"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); doLookupFollowUp(); } }}
                  className="flex-1 px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
                />
                <button
                  type="button"
                  data-testid="follow-up-search-btn"
                  onClick={doLookupFollowUp}
                  disabled={followUpBusy || !followUpOp.trim()}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-teal-500 hover:bg-teal-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-950 font-semibold rounded-xl transition-colors"
                >
                  {followUpBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  {followUpBusy ? 'Searching…' : 'Search'}
                </button>
              </div>
              {followUpError && (
                <div className="mt-3 p-3 bg-red-950/60 border border-red-800/80 rounded-xl text-red-200 text-sm flex items-start justify-between gap-2" data-testid="follow-up-error-alert">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <span>{followUpError}</span>
                  </div>
                  <button
                    type="button"
                    onClick={backToFirstVisit}
                    data-testid="follow-up-create-new-btn"
                    className="shrink-0 text-xs bg-red-500/10 hover:bg-red-500/20 border border-red-500/40 text-red-200 px-3 py-1.5 rounded-lg whitespace-nowrap"
                  >
                    Create New Patient
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Follow-up patient identity card + previous visits + Continue button */}
        {!isEdit && visitType === 'FOLLOW_UP' && followUpLookup && !form.patient_name && (
          <div className="space-y-5" data-testid="follow-up-patient-block">
            <div className="bg-teal-950/40 border border-teal-800/60 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-4 h-4 text-teal-300" />
                <h3 className="text-sm font-semibold text-white">Existing Patient Found</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                <KVReadOnly label="Patient Name" value={followUpLookup.patient?.patient_name} testId="follow-up-name" />
                <KVReadOnly label="OP No." value={followUpLookup.patient?.op_number} testId="follow-up-op" />
                <KVReadOnly label="Village" value={followUpLookup.patient?.village || '—'} />
                <KVReadOnly label="Consultant" value={followUpLookup.defaults?.consultant_name} />
                <KVReadOnly label="Marketing Executive" value={followUpLookup.defaults?.marketing_executive_name} />
                <KVReadOnly label="Referral Doctor" value={followUpLookup.defaults?.referral_doctor_name} />
              </div>
            </div>

            {/* Previous visits */}
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <History className="w-4 h-4 text-slate-400" />
                <h3 className="text-sm font-semibold text-white">Previous Visits ({followUpLookup.history?.length || 0})</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                    <tr>
                      <th className="text-left py-2 pr-3 font-medium">Date</th>
                      <th className="text-left py-2 pr-3 font-medium">Referral ID</th>
                      <th className="text-left py-2 pr-3 font-medium">Visit Type</th>
                      {canSeeTotals && <th className="text-right py-2 pr-3 font-medium">Referral Amount</th>}
                      {canSeeTotals && <th className="text-left py-2 font-medium">Payment</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/70">
                    {followUpLookup.history?.map((h) => (
                      <tr key={h.id} data-testid={`follow-up-history-${h.referral_id}`}>
                        <td className="py-2 pr-3 text-slate-300 text-xs whitespace-nowrap">{h.referral_date || (h.created_at || '').slice(0,10)}</td>
                        <td className="py-2 pr-3 font-mono text-teal-300 text-xs">{h.referral_id}</td>
                        <td className="py-2 pr-3 text-slate-300 text-xs">{h.visit_type === 'FOLLOW_UP' ? 'Follow-up' : 'First Visit'}</td>
                        {canSeeTotals && <td className="py-2 pr-3 text-right text-slate-200 text-xs whitespace-nowrap">₹{formatINR(h.final_referral_amount)}</td>}
                        {canSeeTotals && <td className="py-2 text-xs"><span className={`px-2 py-0.5 rounded ${h.payment_status === 'paid' ? 'bg-emerald-900/40 text-emerald-300' : 'bg-amber-900/40 text-amber-300'}`}>{h.payment_status === 'paid' ? 'Paid' : 'Pending'}</span></td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5">
              <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                <div className="flex-1">
                  <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1">Follow-up Date *</label>
                  <div className="relative">
                    <Calendar className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      data-testid="follow-up-date-input"
                      type="date"
                      value={followUpDate}
                      onChange={(e) => setFollowUpDate(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="follow-up-continue-btn"
                  onClick={continueToFollowUpEntry}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-xl transition-colors"
                >
                  Continue to Follow-up Entry
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {(!shouldShowSearchOnly && !(visitType === 'FOLLOW_UP' && followUpLookup && !form.patient_name)) && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Patient Information */}
          {showPatient && (
            <Section title="Patient Information" icon={<User className="w-5 h-5" />} locked={identityLocked}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Patient Name *" error={validations.patient_name}>
                  <input
                    data-testid="patient-name-input"
                    type="text"
                    autoFocus={!identityLocked}
                    disabled={identityLocked}
                    value={form.patient_name}
                    onChange={(e) => update({ patient_name: e.target.value })}
                    placeholder="Full name"
                    className={inputClass('patient_name', identityLocked)}
                  />
                </Field>
                <Field label="Referral Date *" error={validations.referral_date}>
                  <div className="relative">
                    <Calendar className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      data-testid="referral-date-input"
                      type="date"
                      disabled={patientLocked}
                      value={form.referral_date || ''}
                      onChange={(e) => update({ referral_date: e.target.value })}
                      className={`${inputClass('referral_date', patientLocked)} pl-9`}
                    />
                  </div>
                </Field>
                <Field label="OP Number *" error={validations.op_number}>
                  <input
                    data-testid="op-number-input"
                    type="text"
                    disabled={identityLocked}
                    value={form.op_number}
                    onChange={(e) => update({ op_number: e.target.value })}
                    placeholder="e.g. OP12345"
                    className={inputClass('op_number', identityLocked)}
                  />
                </Field>
                <Field label="Village">
                  <input
                    data-testid="village-input"
                    type="text"
                    disabled={identityLocked}
                    value={form.village}
                    onChange={(e) => update({ village: e.target.value })}
                    placeholder="Optional"
                    className={inputClass('village', identityLocked)}
                  />
                </Field>
              </div>
            </Section>
          )}

          {/* Referral Information */}
          {showPatient && (
            <Section title="Referral Information" icon={<UserPlus className="w-5 h-5" />} locked={patientLocked}>
              <div className="space-y-4">
                <Field label="Referral Doctor *" error={validations.referral_doctor_id}>
                  <SearchableSelect
                    testId="referral-doctor-select"
                    options={doctors}
                    value={form.referral_doctor_id}
                    onChange={(id) => update({ referral_doctor_id: id })}
                    placeholder="Search active referral doctors…"
                    error={!!validations.referral_doctor_id}
                    disabled={patientLocked}
                  />
                </Field>
                <Field label="Marketing Executive *" error={validations.marketing_executive_id}>
                  <SearchableSelect
                    testId="marketing-executive-select"
                    options={executives}
                    value={form.marketing_executive_id}
                    onChange={(id) => update({ marketing_executive_id: id })}
                    placeholder="Search active marketing executives…"
                    error={!!validations.marketing_executive_id}
                    disabled={patientLocked}
                  />
                </Field>
                <Field label="Consultant *" error={validations.consultant_id}>
                  <SearchableSelect
                    testId="consultant-select"
                    options={consultants}
                    value={form.consultant_id}
                    onChange={(id) => update({ consultant_id: id })}
                    placeholder="Search active in-house consultants…"
                    error={!!validations.consultant_id}
                    disabled={patientLocked}
                  />
                </Field>
              </div>
            </Section>
          )}

          {/* Scan */}
          {showScan && (
            <Section title="Scan" icon={<ScanLine className="w-5 h-5" />} subtitle={isAdmin ? "Enter Scan Total. Referral amount is set by the Manager." : "Enter Scan Total Amount only. Manager sets the referral amount."}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <MoneyField label="Scan Total Amount" value={form.scan_total_amount} onChange={(v) => update({ scan_total_amount: v })} error={validations.scan_total_amount} testId="scan-total-input" disabled={!canEditScan} />
                {isAdmin && (
                  <MoneyField label="Scan Referral Amount (manager override)" value={form.scan_referral_amount} onChange={(v) => update({ scan_referral_amount: v })} error={validations.scan_referral_amount} testId="scan-referral-input" disabled={!canEditScan} />
                )}
                {!isAdmin && canSeeTotals && (
                  <ComputedField label="Scan Referral Amount" value={isEdit && loadedReferral ? loadedReferral.scan_referral_amount : 0} testId="scan-referral-display" locked />
                )}
              </div>
            </Section>
          )}

          {/* Procedures (Reception + Admin) */}
          {canManageProcedures && showScan && (
            <Section title="Procedures" icon={<ClipboardList className="w-5 h-5" />} subtitle="Add one or more procedures with their manual amounts. Manager sets the referral percentage or goodwill later.">
              {procedures.length > 0 && (
                <div className="mb-4 rounded-xl border border-slate-800 overflow-hidden" data-testid="procedures-list">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-950/60 text-[10px] uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Procedure</th>
                        <th className="text-right px-3 py-2 font-medium">Amount</th>
                        <th className="text-right px-3 py-2 font-medium">Referral</th>
                        <th className="px-3 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/70">
                      {procedures.map((p) => (
                        <tr key={p.id || p._local} className="text-slate-200">
                          <td className="px-3 py-2">{p.procedure_name}</td>
                          <td className="px-3 py-2 text-right tabular-nums">₹{formatINR(p.procedure_amount)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-teal-300">₹{formatINR(p.referral_amount || 0)}</td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => removeProcedureRow(p)}
                              data-testid={`procedure-remove-${(p.id || p._local || '').slice(0, 12)}`}
                              className="text-slate-500 hover:text-rose-400 transition-colors"
                              title="Remove procedure"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_auto] gap-3 items-end">
                <Field label="Procedure Name">
                  <input
                    data-testid="procedure-name-input"
                    type="text"
                    value={procDraftName}
                    onChange={(e) => setProcDraftName(e.target.value)}
                    placeholder="e.g. ECG, Dressing, Minor Procedure"
                    className={inputBase + ' border-slate-800'}
                  />
                </Field>
                <MoneyField
                  label="Amount"
                  value={procDraftAmount}
                  onChange={(v) => setProcDraftAmount(v)}
                  testId="procedure-amount-input"
                />
                <button
                  type="button"
                  onClick={addProcedureRow}
                  data-testid="procedure-add-btn"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold text-sm rounded-xl transition-all"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>
            </Section>
          )}

          {/* Laboratory */}
          {showLab && (
            <Section title="Laboratory" icon={<Beaker className="w-5 h-5" />} subtitle={isAdmin ? undefined : "Enter Lab Total and Kit Cost. Referral amount is set by the Manager."}>
              {isAdmin && (
                <MethodToggle
                  testId="lab-method-toggle"
                  value={form.lab_method}
                  onChange={(m) => update({ lab_method: m })}
                  disabled={!canEditLab}
                />
              )}
              {isAdmin && form.lab_method === 'goodwill' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <MoneyField
                    label="Lab Goodwill Amount"
                    value={form.lab_goodwill_amount}
                    onChange={(v) => update({ lab_goodwill_amount: v })}
                    error={validations.lab_goodwill_amount}
                    testId="lab-goodwill-input"
                    disabled={!canEditLab}
                  />
                  <ComputedField label="Lab Referral Amount" value={calc.labRef} highlight testId="lab-referral-display" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <MoneyField label="Lab Total Amount" value={form.lab_total_amount} onChange={(v) => update({ lab_total_amount: v })} error={validations.lab_total_amount} testId="lab-total-input" disabled={!canEditLab} />
                  <MoneyField label="Lab Kit Cost" value={form.lab_kit_amount} onChange={(v) => update({ lab_kit_amount: v })} error={validations.lab_kit_amount} testId="lab-kit-input" disabled={!canEditLab} />
                  <ComputedField label="Lab Net Amount" value={calc.labNet} testId="lab-net-display" />
                  {isAdmin && (
                    <PctField label="Referral Percentage" value={form.lab_percentage} onChange={(v) => update({ lab_percentage: v })} error={validations.lab_percentage} testId="lab-pct-input" disabled={!canEditLab} />
                  )}
                  {isAdmin && (
                    <div className="md:col-span-2">
                      <ComputedField label="Lab Referral Amount" value={calc.labRef} highlight testId="lab-referral-display" />
                    </div>
                  )}
                  {!isAdmin && canSeeTotals && (
                    <div className="md:col-span-2">
                      <ComputedField label="Lab Referral Amount" value={isEdit && loadedReferral ? loadedReferral.lab_referral_amount : 0} testId="lab-referral-display" locked />
                    </div>
                  )}
                </div>
              )}
            </Section>
          )}

          {/* Pharmacy */}
          {showPharmacy && (
            <Section title="Pharmacy" icon={<Pill className="w-5 h-5" />} subtitle={isAdmin ? undefined : "Enter Pharmacy Total and Kit Cost. Referral amount is set by the Manager."}>
              {isAdmin && (
                <MethodToggle
                  testId="pharmacy-method-toggle"
                  value={form.pharmacy_method}
                  onChange={(m) => update({ pharmacy_method: m })}
                  disabled={!canEditPharmacy}
                />
              )}
              {isAdmin && form.pharmacy_method === 'goodwill' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <MoneyField
                    label="Pharmacy Goodwill Amount"
                    value={form.pharmacy_goodwill_amount}
                    onChange={(v) => update({ pharmacy_goodwill_amount: v })}
                    error={validations.pharmacy_goodwill_amount}
                    testId="pharmacy-goodwill-input"
                    disabled={!canEditPharmacy}
                  />
                  <ComputedField label="Pharmacy Referral Amount" value={calc.phRef} highlight testId="pharmacy-referral-display" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <MoneyField label="Pharmacy Total Amount" value={form.pharmacy_total_amount} onChange={(v) => update({ pharmacy_total_amount: v })} error={validations.pharmacy_total_amount} testId="pharmacy-total-input" disabled={!canEditPharmacy} />
                  <MoneyField label="Pharmacy Kit Cost" value={form.pharmacy_kit_amount} onChange={(v) => update({ pharmacy_kit_amount: v })} error={validations.pharmacy_kit_amount} testId="pharmacy-kit-input" disabled={!canEditPharmacy} />
                  <ComputedField label="Pharmacy Net Amount" value={calc.phNet} testId="pharmacy-net-display" />
                  {isAdmin && (
                    <PctField label="Referral Percentage" value={form.pharmacy_percentage} onChange={(v) => update({ pharmacy_percentage: v })} error={validations.pharmacy_percentage} testId="pharmacy-pct-input" disabled={!canEditPharmacy} />
                  )}
                  {isAdmin && (
                    <div className="md:col-span-2">
                      <ComputedField label="Pharmacy Referral Amount" value={calc.phRef} highlight testId="pharmacy-referral-display" />
                    </div>
                  )}
                  {!isAdmin && canSeeTotals && (
                    <div className="md:col-span-2">
                      <ComputedField label="Pharmacy Referral Amount" value={isEdit && loadedReferral ? loadedReferral.pharmacy_referral_amount : 0} testId="pharmacy-referral-display" locked />
                    </div>
                  )}
                </div>
              )}
            </Section>
          )}

          {/* IP */}
          {showIP && (
            <Section title="IP" icon={<BedDouble className="w-5 h-5" />} subtitle={isAdmin ? undefined : "Enter IP Amount and Deductibles only. Referral amount is set by the Manager."}>
              {isAdmin && (
                <MethodToggle
                  testId="ip-method-toggle"
                  value={form.ip_method}
                  onChange={(m) => update({ ip_method: m })}
                  disabled={!canEditIP}
                />
              )}
              {isAdmin && form.ip_method === 'goodwill' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <MoneyField
                    label="IP Goodwill Amount"
                    value={form.ip_goodwill_amount}
                    onChange={(v) => update({ ip_goodwill_amount: v })}
                    error={validations.ip_goodwill_amount}
                    testId="ip-goodwill-input"
                    disabled={!canEditIP}
                  />
                  <ComputedField label="IP Referral Amount" value={calc.ipRef} highlight testId="ip-referral-display" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <MoneyField label="IP Amount" value={form.ip_amount} onChange={(v) => update({ ip_amount: v })} error={validations.ip_amount} testId="ip-amount-input" disabled={!canEditIP} />
                  <MoneyField label="Deductibles" value={form.ip_deductibles} onChange={(v) => update({ ip_deductibles: v })} error={validations.ip_deductibles} testId="ip-deductibles-input" disabled={!canEditIP} />
                  <ComputedField label="IP Net Amount" value={calc.ipNet} testId="ip-net-display" />
                  {isAdmin && (
                    <PctField label="Referral Percentage" value={form.ip_percentage} onChange={(v) => update({ ip_percentage: v })} error={validations.ip_percentage} testId="ip-pct-input" disabled={!canEditIP} />
                  )}
                  {isAdmin && (
                    <div className="md:col-span-2">
                      <ComputedField label="IP Referral Amount" value={calc.ipRef} highlight testId="ip-referral-display" />
                    </div>
                  )}
                  {!isAdmin && canSeeTotals && (
                    <div className="md:col-span-2">
                      <ComputedField label="IP Referral Amount" value={isEdit && loadedReferral ? loadedReferral.ip_referral_amount : 0} testId="ip-referral-display" locked />
                    </div>
                  )}
                </div>
              )}
            </Section>
          )}

          {/* Final Amount — hidden for reception/lab/pharmacy who should not see totals */}
          {canSeeTotals && (isAdmin || (!isEdit && user?.role === 'staff')) && (
            <div className="bg-gradient-to-br from-teal-950 via-slate-900 to-emerald-950/60 border border-teal-800/60 rounded-2xl p-6 sm:p-8 shadow-xl">
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-teal-300 mb-2">Final Referral Amount</div>
                  <div data-testid="final-referral-amount" className="text-3xl sm:text-5xl font-bold text-white flex items-center gap-2">
                    <IndianRupee className="w-6 h-6 sm:w-8 sm:h-8 text-teal-300" />
                    <span className="tabular-nums">{formatINR(calc.final)}</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-2">
                    = Scan ₹{formatINR(calc.scanRef)} + Lab ₹{formatINR(calc.labRef)} + Pharmacy ₹{formatINR(calc.phRef)} + IP ₹{formatINR(calc.ipRef)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Section-level running total for lab_staff / pharmacist — HIDDEN because they should not see totals */}
          {false && (isLabStaff || isPharmacist) && isEdit && (
            <div className="bg-gradient-to-br from-teal-950 via-slate-900 to-emerald-950/60 border border-teal-800/60 rounded-2xl p-6 shadow-xl">
              <div className="text-[10px] uppercase tracking-widest text-teal-300 mb-2">
                {isLabStaff ? 'Your Lab Contribution' : 'Your Pharmacy Contribution'}
              </div>
              <div data-testid="section-contribution-amount" className="text-3xl font-bold text-white flex items-center gap-2">
                <IndianRupee className="w-6 h-6 text-teal-300" />
                <span className="tabular-nums">{formatINR(isLabStaff ? calc.labRef : calc.phRef)}</span>
              </div>
            </div>
          )}

          {/* Sticky footer save bar */}
          <div className="fixed bottom-0 inset-x-0 z-30 bg-slate-950/95 border-t border-slate-800 backdrop-blur-xl">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
              <div className="text-xs text-slate-400 hidden sm:block">
                {isEdit && loadedReferral ? (
                  <>Editing <span className="font-mono text-teal-400">{loadedReferral.referral_id}</span></>
                ) : (
                  'All amounts in ₹ (INR). Rounded to 2 decimals on save.'
                )}
              </div>
              <button
                type="submit"
                data-testid="save-referral-btn"
                disabled={saving || hasErrors}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-500 hover:bg-teal-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-950 font-semibold text-sm rounded-xl shadow-lg shadow-teal-500/20 transition-all"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? 'Saving…' : isEdit ? 'Update Referral' : 'Save Referral'}
              </button>
            </div>
          </div>
        </form>
        )}
      </main>
    </div>
  );
}

function KVReadOnly({ label, value, testId }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div data-testid={testId} className="text-slate-100 font-medium truncate">{value || '—'}</div>
    </div>
  );
}

/* ---------- helpers ---------- */

function Section({ title, subtitle, icon, children, locked }) {
  return (
    <section className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 sm:p-6 shadow-sm">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-teal-500/10 border border-teal-500/20 text-teal-400 flex items-center justify-center shrink-0">
          {icon}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-white">{title}</h3>
            {locked && (
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-slate-800/80 border border-slate-700/80 text-slate-400">
                <Lock className="w-3 h-3" /> read-only
              </span>
            )}
          </div>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function MethodToggle({ value, onChange, disabled, testId }) {
  const opts = [
    { key: 'percentage', label: 'Percentage', Icon: Percent },
    { key: 'goodwill', label: 'Goodwill / Direct Amount', Icon: HandCoins },
  ];
  return (
    <div data-testid={testId} className="inline-flex items-center bg-slate-950 border border-slate-800 rounded-xl p-1 mb-4">
      {opts.map(({ key, label, Icon }) => {
        const active = value === key;
        return (
          <button
            key={key}
            type="button"
            disabled={disabled}
            data-testid={`${testId}-${key}`}
            onClick={() => onChange(key)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
              active
                ? 'bg-teal-500 text-slate-950 shadow-sm shadow-teal-500/20'
                : 'text-slate-400 hover:text-white'
            } ${disabled && !active ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        );
      })}
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">{label}</label>
      {children}
      {error && (
        <div className="text-[11px] text-red-400 mt-1 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </div>
      )}
    </div>
  );
}

function MoneyField({ label, value, onChange, error, testId, disabled }) {
  return (
    <Field label={label} error={error}>
      <div className="relative">
        <IndianRupee className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          data-testid={testId}
          type="number"
          min="0"
          step="0.01"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0.00"
          className={`w-full pl-8 pr-3.5 py-2.5 bg-slate-950 border rounded-xl text-sm text-white placeholder-slate-600 focus:outline-none focus:border-teal-500 transition-colors ${
            error ? 'border-red-800/70' : 'border-slate-800'
          } ${disabled ? 'opacity-60 cursor-not-allowed bg-slate-900' : ''}`}
        />
      </div>
    </Field>
  );
}

function PctField({ label, value, onChange, error, testId, disabled }) {
  return (
    <Field label={label} error={error}>
      <div className="relative">
        <input
          data-testid={testId}
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0"
          className={`w-full pl-3.5 pr-8 py-2.5 bg-slate-950 border rounded-xl text-sm text-white placeholder-slate-600 focus:outline-none focus:border-teal-500 transition-colors ${
            error ? 'border-red-800/70' : 'border-slate-800'
          } ${disabled ? 'opacity-60 cursor-not-allowed bg-slate-900' : ''}`}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs">%</span>
      </div>
    </Field>
  );
}

function ComputedField({ label, value, highlight, testId, locked }) {
  return (
    <Field label={locked ? `${label} (set by Manager)` : `${label} (auto)`}>
      <div
        data-testid={testId}
        className={`px-3.5 py-2.5 rounded-xl text-sm font-mono tabular-nums flex items-center gap-1 border ${
          highlight
            ? 'bg-teal-950/40 border-teal-800/60 text-teal-200 font-semibold'
            : locked
            ? 'bg-slate-900/60 border-slate-800/60 text-slate-500 italic'
            : 'bg-slate-950/60 border-slate-800/60 text-slate-300'
        }`}
      >
        {locked && <Lock className="w-3 h-3 opacity-70" />}
        {!locked && <IndianRupee className="w-3.5 h-3.5 opacity-70" />}
        {formatINR(value)}
      </div>
    </Field>
  );
}
